// Second round of the crown lab: five takes each on the two picks from the
// first round, Laurel (3) and Viking (12). Picked for play (head-styles.ts):
// Gilded Shine, the Royale's crown, and Jarl, sold as Rich Viking. Same contract as crown-designs.ts:
// the caller has translated to the head and saves/restores around the call,
// every draw rotates with the head (local -y is forward) and stays inside
// ~1.7r.

import {
  type Ctx, type CrownDesign, type CrownDraw,
  TAU, HALF, INK, GOLD, GOLD_DARK, PEARL, RUBY,
  mix, lighten, darken, lw, goldGrad, goldRadial, disc, face, sparkle, band,
} from "./crown-designs";

export interface CrownVariant extends CrownDesign { id: string }

// ---------- laurel parts ----------

interface LeafLook { light: string; dark: string; edge: string; vein: string }
const GILT: LeafLook = { light: "#ffe58a", dark: "#d39a18", edge: INK, vein: "#8a5a00" };

// A two-tone leaf pointing along +x, centred on its middle: lit upper half,
// shaded lower half, a midrib, and side veins only when it is big enough to
// show them (at game size they would just be mud).
function leaf(ctx: Ctx, r: number, len: number, wid: number, look: LeafLook, lift = 0) {
  const light = lift > 0 ? mix(look.light, "#fffbe0", lift) : look.light;
  ctx.fillStyle = light;
  ctx.beginPath(); ctx.moveTo(-len, 0); ctx.quadraticCurveTo(0, -wid * 2, len, 0); ctx.closePath(); ctx.fill();
  ctx.fillStyle = look.dark;
  ctx.beginPath(); ctx.moveTo(-len, 0); ctx.quadraticCurveTo(0, wid * 2, len, 0); ctx.closePath(); ctx.fill();
  ctx.strokeStyle = look.edge; ctx.lineWidth = lw(r, 0.05);
  ctx.beginPath(); ctx.moveTo(-len, 0); ctx.quadraticCurveTo(0, -wid * 2, len, 0); ctx.quadraticCurveTo(0, wid * 2, -len, 0); ctx.stroke();
  ctx.strokeStyle = look.vein; ctx.lineWidth = lw(r, 0.035);
  ctx.beginPath(); ctx.moveTo(-len * 0.85, 0); ctx.lineTo(len * 0.75, 0);
  if (len > 6) {
    for (const u of [-0.35, 0.05, 0.42]) {
      ctx.moveTo(u * len, 0); ctx.lineTo((u + 0.22) * len, -wid * 0.75);
      ctx.moveTo(u * len, 0); ctx.lineTo((u + 0.22) * len, wid * 0.75);
    }
  }
  ctx.stroke();
}

interface Branch {
  nodes: number; from: number; step: number; // angles from the back, radians
  inner: number; outer: number; tilt: number; // radii in r, leaf splay
  len: number; wid: number; // leaf size in r
  tip?: boolean; // one more leaf closing each branch, pointing forward
}
// Calls back for every leaf of both branches. The branches start at the back
// of the head (local +y) and grow forward round each side.
function eachLeaf(
  r: number, b: Branch,
  cb: (x: number, y: number, rot: number, s: number, k: number) => void,
) {
  for (const s of [-1, 1]) {
    for (let k = 0; k < b.nodes; k++) {
      const a = HALF - s * (b.from + k * b.step);
      const tang = a - s * HALF; // pointing forward along the branch
      for (const [rad, tilt] of [[b.inner, -b.tilt], [b.outer, b.tilt]]) {
        cb(Math.cos(a) * r * rad, Math.sin(a) * r * rad, tang + s * tilt, s, k);
      }
    }
    if (b.tip) {
      const a = HALF - s * (b.from + b.nodes * b.step - b.step * 0.3);
      const mid = (b.inner + b.outer) / 2;
      cb(Math.cos(a) * r * mid, Math.sin(a) * r * mid, a - s * HALF, s, b.nodes);
    }
  }
}
function branchSpan(b: Branch): [number, number] {
  return [b.from - 0.1, b.from + (b.nodes - 1) * b.step + (b.tip ? b.step * 0.5 : 0)];
}
// A dark ring under the wreath: at game size it is what makes the wreath read
// as one bold gold loop rather than a scatter of specks.
function underlay(ctx: Ctx, r: number, b: Branch, full = false) {
  const mid = (b.inner + b.outer) / 2;
  ctx.strokeStyle = "rgba(40,26,0,.85)"; ctx.lineWidth = r * (b.outer - b.inner + 0.3); ctx.lineCap = "round";
  if (full) { ctx.beginPath(); ctx.arc(0, 0, r * mid, 0, TAU); ctx.stroke(); return; }
  const [a0, a1] = branchSpan(b);
  for (const s of [-1, 1]) {
    ctx.beginPath(); ctx.arc(0, 0, r * mid, HALF - s * a0, HALF - s * a1, s > 0); ctx.stroke();
  }
}
function stems(ctx: Ctx, r: number, b: Branch, colour: string) {
  const mid = (b.inner + b.outer) / 2;
  const [a0, a1] = branchSpan(b);
  ctx.strokeStyle = colour; ctx.lineWidth = lw(r, 0.07); ctx.lineCap = "round";
  for (const s of [-1, 1]) {
    ctx.beginPath(); ctx.arc(0, 0, r * mid, HALF - s * a0, HALF - s * a1, s > 0); ctx.stroke();
  }
}
function drawLeaves(ctx: Ctx, r: number, b: Branch, look: LeafLook, lift: (k: number) => number = () => 0) {
  eachLeaf(r, b, (x, y, rot, _s, k) => {
    ctx.save(); ctx.translate(x, y); ctx.rotate(rot);
    leaf(ctx, r, r * b.len, r * b.wid, look, lift(k));
    ctx.restore();
  });
}
// The short ribbon bow behind the head, in the worm's colour.
function bow(ctx: Ctx, r: number, color: string) {
  ctx.fillStyle = lighten(color, 0.1); ctx.strokeStyle = darken(color, 0.5); ctx.lineWidth = lw(r, 0.06); ctx.lineJoin = "round";
  for (const s of [-1, 1]) {
    ctx.beginPath();
    ctx.moveTo(0, r * 0.84); ctx.lineTo(s * r * 0.34, r * 1.4); ctx.lineTo(s * r * 0.12, r * 1.3); ctx.closePath();
    ctx.fill(); ctx.stroke();
    ctx.beginPath(); ctx.ellipse(s * r * 0.2, r * 0.84, r * 0.18, r * 0.11, 0, 0, TAU); ctx.fill(); ctx.stroke();
  }
  disc(ctx, 0, r * 0.84, r * 0.13, lighten(color, 0.1)); ctx.stroke();
}

// L1. Gilded: fuller two-tone gold leaves with veins on a dark underlay, so
// it reads as a bold gold ring at game size; a shine rolls along it.
const L_FULL: Branch = { nodes: 8, from: 0.28, step: 0.3, inner: 0.66, outer: 1.04, tilt: 0.5, len: 0.34, wid: 0.15, tip: true };
const gilded: CrownDraw = (ctx, r, dir, t, color) => {
  ctx.rotate(dir + HALF);
  face(ctx, r, color, 0.78);
  underlay(ctx, r, L_FULL);
  stems(ctx, r, L_FULL, GOLD_DARK);
  drawLeaves(ctx, r, L_FULL, GILT, (k) => Math.max(0, Math.sin(t * 1.4 - k * 0.6)) * 0.7);
  bow(ctx, r, color);
};

// L6. Gilded Shine: Gilded with glints, small four-point stars that flash on
// and off around the wreath, each on its own beat, so it reads as polished
// gold catching the light. Sharp blinks (a high power of a sine), lit additively.
const GLINTS = [
  { a: -2.5, r: 0.98, p: 0.0, s: 1.0 },
  { a: 1.05, r: 0.9, p: 2.1, s: 1.0 },
  { a: -0.25, r: 1.0, p: 4.2, s: 0.9 },
];
function glint(ctx: Ctx, x: number, y: number, size: number, k: number) {
  if (k <= 0.02) return;
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(Math.PI / 4 * 0.5);
  ctx.globalCompositeOperation = "lighter";
  const g = ctx.createRadialGradient(0, 0, 0, 0, 0, size * 1.2);
  g.addColorStop(0, `rgba(255, 246, 200, ${0.55 * k})`);
  g.addColorStop(1, "rgba(255, 210, 63, 0)");
  ctx.fillStyle = g;
  ctx.beginPath(); ctx.arc(0, 0, size * 1.2, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = `rgba(255, 255, 240, ${k})`;
  const w = size * 0.16;
  for (const rot of [0, Math.PI / 2]) {
    ctx.save(); ctx.rotate(rot);
    ctx.beginPath(); ctx.moveTo(0, -size); ctx.lineTo(w, 0); ctx.lineTo(0, size); ctx.lineTo(-w, 0); ctx.closePath(); ctx.fill();
    ctx.restore();
  }
  ctx.restore();
}
export const gildedShine: CrownDraw = (ctx, r, dir, t, color) => {
  ctx.save();
  gilded(ctx, r, dir, t, color);
  ctx.restore();
  ctx.save();
  ctx.rotate(dir + HALF);
  for (const g of GLINTS) {
    // (calm: one flash every second or two across the three, not a constant twinkle)
    const k = Math.pow(Math.max(0, Math.sin(t * 1.1 + g.p)), 6);
    glint(ctx, Math.cos(g.a) * g.r * r, Math.sin(g.a) * g.r * r, r * 0.46 * g.s * (0.55 + 0.45 * k), k);
  }
  ctx.restore();
};

// L2. Berries: a true green laurel with gold berries tucked between the leaf
// pairs; the berries swell in a slow wave, as if the wreath were ripening.
const GREEN: LeafLook = { light: "#a6cf5e", dark: "#4f7d2a", edge: "#1f3410", vein: "#2c4a14" };
const L_BERRY: Branch = { nodes: 7, from: 0.32, step: 0.33, inner: 0.66, outer: 1.02, tilt: 0.48, len: 0.32, wid: 0.14, tip: true };
const berries: CrownDraw = (ctx, r, dir, t, color) => {
  ctx.rotate(dir + HALF);
  face(ctx, r, color, 0.78);
  underlay(ctx, r, L_BERRY);
  stems(ctx, r, L_BERRY, "#3b5a1c");
  drawLeaves(ctx, r, L_BERRY, GREEN);
  for (const s of [-1, 1]) {
    for (let k = 0; k < L_BERRY.nodes; k++) {
      const a = HALF - s * (L_BERRY.from + (k + 0.5) * L_BERRY.step);
      const swell = 1 + 0.18 * Math.sin(t * 1.8 - k * 0.8);
      for (const d of [0.78, 0.94]) {
        const x = Math.cos(a) * r * d, y = Math.sin(a) * r * d;
        disc(ctx, x, y, r * 0.12 * swell, INK);
        disc(ctx, x, y, r * 0.085 * swell, GOLD);
        disc(ctx, x - r * 0.025, y - r * 0.025, r * 0.03, "#fffbe0");
      }
    }
  }
  // a gold knot where the branches meet behind
  disc(ctx, 0, r * 0.85, r * 0.17, INK);
  disc(ctx, 0, r * 0.85, r * 0.12, goldRadial(ctx, 0, r * 0.9));
};

// L3. Clasp: the wreath closes all the way round, the two branches meeting at
// the front in a gold clasp set with a gem in the worm's colour.
const L_CLASP: Branch = { nodes: 9, from: 0.14, step: 0.31, inner: 0.66, outer: 1.0, tilt: 0.5, len: 0.31, wid: 0.14 };
const clasp: CrownDraw = (ctx, r, dir, t, color) => {
  ctx.rotate(dir + HALF);
  face(ctx, r, color, 0.78);
  underlay(ctx, r, L_CLASP, true);
  stems(ctx, r, L_CLASP, GOLD_DARK);
  drawLeaves(ctx, r, L_CLASP, GILT);
  disc(ctx, 0, r * 0.83, r * 0.14, INK);
  disc(ctx, 0, r * 0.83, r * 0.09, GOLD);
  ctx.save();
  ctx.translate(0, -r * 0.84);
  ctx.fillStyle = goldGrad(ctx, -r * 0.3, r * 0.3); ctx.strokeStyle = INK; ctx.lineWidth = lw(r, 0.09);
  ctx.beginPath(); ctx.ellipse(0, 0, r * 0.34, r * 0.27, 0, 0, TAU); ctx.fill(); ctx.stroke();
  const g = ctx.createRadialGradient(-r * 0.06, -r * 0.06, 0, 0, 0, r * 0.2);
  g.addColorStop(0, lighten(color, 0.6)); g.addColorStop(0.6, color); g.addColorStop(1, darken(color, 0.4));
  ctx.fillStyle = g;
  ctx.beginPath(); ctx.ellipse(0, 0, r * 0.21, r * 0.16, 0, 0, TAU); ctx.fill();
  ctx.strokeStyle = darken(color, 0.55); ctx.lineWidth = lw(r, 0.05); ctx.stroke();
  for (const x of [-0.27, 0.27]) disc(ctx, x * r, 0, r * 0.05, PEARL);
  sparkle(ctx, r * 0.08, -r * 0.08, r * 0.3, Math.max(0, Math.sin(t * 1.7)));
  ctx.restore();
};

// L4. Victor: a bright gold wreath in a soft breathing halo, little gold
// sparks drifting off it and falling away behind the head.
const L_VICTOR: Branch = { nodes: 7, from: 0.3, step: 0.33, inner: 0.66, outer: 1.02, tilt: 0.48, len: 0.33, wid: 0.15, tip: true };
const victor: CrownDraw = (ctx, r, dir, t, color) => {
  ctx.rotate(dir + HALF);
  const pulse = 0.75 + 0.25 * Math.sin(t * 1.6);
  const halo = ctx.createRadialGradient(0, 0, r * 0.6, 0, 0, r * 1.6);
  halo.addColorStop(0, `rgba(255,215,90,${(0.45 * pulse).toFixed(3)})`); halo.addColorStop(1, "rgba(255,215,90,0)");
  disc(ctx, 0, 0, r * 1.6, halo);
  face(ctx, r, color, 0.78);
  underlay(ctx, r, L_VICTOR);
  stems(ctx, r, L_VICTOR, GOLD_DARK);
  drawLeaves(ctx, r, L_VICTOR, { ...GILT, light: "#fff0a8", dark: "#e6ad22" });
  bow(ctx, r, color);
  const N = 9;
  for (let i = 0; i < N; i++) {
    const p = (t * 0.45 + i / N) % 1;
    const s = i % 2 ? 1 : -1;
    const a = HALF - s * (0.5 + ((i * 0.618) % 1) * 1.9);
    const x = Math.cos(a) * r * 1.0 * (1 + p * 0.15);
    const y = Math.sin(a) * r * 1.0 + p * r * 0.55;
    ctx.save();
    ctx.globalAlpha = Math.sin(p * Math.PI);
    disc(ctx, x, y, r * 0.08 * (1 - p * 0.5), "#ffe27a");
    ctx.restore();
  }
};

// L5. Streamers: fewer, bigger leaves for a bolder silhouette, and long
// ribbon tails in the worm's colour fluttering out behind.
const L_BOLD: Branch = { nodes: 6, from: 0.36, step: 0.37, inner: 0.66, outer: 1.04, tilt: 0.5, len: 0.4, wid: 0.17, tip: true };
const streamers: CrownDraw = (ctx, r, dir, t, color) => {
  ctx.rotate(dir + HALF);
  ctx.lineCap = "round"; ctx.lineJoin = "round";
  const tail = (s: number) => {
    ctx.beginPath();
    for (let i = 0; i <= 12; i++) {
      const u = i / 12;
      const x = s * (0.12 + 0.36 * u) * r + Math.sin(u * 4.2 - t * 4 + s) * r * 0.12 * u;
      const y = r * (0.86 + 0.72 * u);
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
  };
  for (const s of [-1, 1]) {
    tail(s); ctx.strokeStyle = darken(color, 0.5); ctx.lineWidth = r * 0.3; ctx.stroke();
    tail(s); ctx.strokeStyle = color; ctx.lineWidth = r * 0.2; ctx.stroke();
    tail(s); ctx.strokeStyle = lighten(color, 0.35); ctx.lineWidth = r * 0.05; ctx.stroke();
  }
  face(ctx, r, color, 0.78);
  underlay(ctx, r, L_BOLD);
  stems(ctx, r, L_BOLD, GOLD_DARK);
  drawLeaves(ctx, r, L_BOLD, GILT, (k) => Math.max(0, Math.sin(t * 1.2 - k * 0.7)) * 0.6);
  disc(ctx, 0, r * 0.86, r * 0.19, darken(color, 0.5));
  disc(ctx, 0, r * 0.86, r * 0.14, lighten(color, 0.1));
};

export const LAUREL_VARIANTS: CrownVariant[] = [
  { id: "L1", name: "Gilded", idea: "fuller two-tone veined leaves, bold dark underlay", draw: gilded },
  { id: "L2", name: "Berries", idea: "green laurel with ripening gold berries", draw: berries },
  { id: "L3", name: "Clasp", idea: "closed wreath, gem clasp in the worm's colour", draw: clasp },
  { id: "L4", name: "Victor", idea: "breathing halo and drifting gold sparks", draw: victor },
  { id: "L5", name: "Streamers", idea: "big bold leaves, fluttering ribbon tails", draw: streamers },
  { id: "L6", name: "Gilded Shine", idea: "Gilded, with glints flashing around the wreath", draw: gildedShine },
];

// ---------- viking parts ----------

interface HelmOpts { trim?: boolean; crest?: boolean; worn?: boolean }
// The helm, front-on with its top leading: steel dome, a bold gold nose ridge
// running over it and down past the band (the part that has to survive at
// game size), and a riveted gold band.
function helm(ctx: Ctx, r: number, o: HelmOpts = {}) {
  ctx.lineJoin = "round";
  const steel = ctx.createLinearGradient(0, -r * 0.9, 0, r * 0.4);
  if (o.worn) { steel.addColorStop(0, "#c9cfd6"); steel.addColorStop(1, "#5d6672"); }
  else { steel.addColorStop(0, "#eef3f9"); steel.addColorStop(1, "#7f8da2"); }
  ctx.fillStyle = steel; ctx.strokeStyle = "#3e4756"; ctx.lineWidth = lw(r, 0.09);
  ctx.beginPath(); ctx.arc(0, r * 0.3, r * 0.92, Math.PI, TAU); ctx.closePath(); ctx.fill(); ctx.stroke();
  ctx.strokeStyle = "rgba(255,255,255,.55)"; ctx.lineWidth = lw(r, 0.07);
  ctx.beginPath(); ctx.arc(0, r * 0.3, r * 0.74, Math.PI * 1.15, Math.PI * 1.4); ctx.stroke();
  if (o.trim) {
    ctx.strokeStyle = INK; ctx.lineWidth = r * 0.22;
    ctx.beginPath(); ctx.arc(0, r * 0.3, r * 0.86, Math.PI, TAU); ctx.stroke();
    ctx.strokeStyle = GOLD; ctx.lineWidth = r * 0.11; ctx.stroke();
  }
  if (o.worn) {
    ctx.strokeStyle = "rgba(30,34,40,.7)"; ctx.lineWidth = lw(r, 0.05);
    ctx.beginPath();
    ctx.moveTo(-r * 0.6, -r * 0.2); ctx.lineTo(-r * 0.38, -r * 0.05);
    ctx.moveTo(r * 0.3, -r * 0.42); ctx.lineTo(r * 0.5, -r * 0.18); ctx.lineTo(r * 0.44, -r * 0.05);
    ctx.stroke();
    disc(ctx, -r * 0.45, -r * 0.32, r * 0.1, "rgba(30,34,40,.35)");
  }
  const top = o.crest ? -r * 0.78 : -r * 0.62;
  const hw = o.crest ? r * 0.17 : r * 0.14;
  ctx.fillStyle = goldGrad(ctx, top, r * 0.3); ctx.strokeStyle = INK; ctx.lineWidth = lw(r, 0.09);
  ctx.beginPath();
  ctx.moveTo(-hw, r * 0.3); ctx.lineTo(-hw, top + hw); ctx.quadraticCurveTo(0, top - hw * 0.6, hw, top + hw); ctx.lineTo(hw, r * 0.3);
  ctx.closePath(); ctx.fill(); ctx.stroke();
  if (o.crest) for (const y of [-0.5, -0.25, 0]) disc(ctx, 0, y * r, r * 0.055, INK);
  band(ctx, r, r * 0.98, r * 0.2, r * 0.48, goldGrad(ctx, r * 0.2, r * 0.68));
  // nasal guard below the band
  ctx.fillStyle = goldGrad(ctx, r * 0.66, r * 1.0); ctx.strokeStyle = INK;
  ctx.beginPath(); ctx.moveTo(-r * 0.13, r * 0.66); ctx.lineTo(r * 0.13, r * 0.66); ctx.lineTo(r * 0.07, r * 1.0); ctx.lineTo(-r * 0.07, r * 1.0); ctx.closePath();
  ctx.fill(); ctx.stroke();
  const rivets = o.worn ? [-0.7, -0.35, 0.7] : [-0.7, -0.35, 0.35, 0.7];
  for (const x of rivets) { disc(ctx, x * r, r * 0.44, r * 0.075, INK); disc(ctx, x * r - r * 0.02, r * 0.42, r * 0.03, "#fff0a8"); }
}
function hornFill(ctx: Ctx, x0: number, y0: number, x1: number, y1: number, tip = "#7d6844") {
  const g = ctx.createLinearGradient(x0, y0, x1, y1);
  g.addColorStop(0, "#f6ecd2"); g.addColorStop(0.7, "#e2cf9f"); g.addColorStop(1, tip);
  return g;
}
// The original sweeping horn, scaled about the helm's side.
function sweptHorn(ctx: Ctx, r: number, s: number, k = 1) {
  ctx.fillStyle = hornFill(ctx, s * r * 0.7, 0, s * r * 1.3 * k, -r * 1.25 * k);
  ctx.strokeStyle = "#4e3f22"; ctx.lineWidth = lw(r, 0.08); ctx.lineJoin = "round";
  ctx.beginPath();
  ctx.moveTo(s * r * 0.72, r * 0.15);
  ctx.quadraticCurveTo(s * r * (0.72 + 0.88 * k), -r * 0.05 * k, s * r * (0.72 + 0.56 * k), -r * 1.25 * k);
  ctx.quadraticCurveTo(s * r * (0.6 + 0.62 * k), -r * 0.4 * k, s * r * 0.6, -r * 0.42);
  ctx.closePath(); ctx.fill(); ctx.stroke();
}

// V1. Ram: thick ridged horns curling round into spirals at the helm's sides.
const ram: CrownDraw = (ctx, r, dir) => {
  ctx.rotate(dir + HALF);
  ctx.lineCap = "round";
  for (const s of [-1, 1]) {
    const cx = s * r * 1.1, cy = -r * 0.12;
    const phi0 = s > 0 ? Math.PI : 0;
    const pts: [number, number, number][] = [];
    const N = 28;
    for (let i = 0; i <= N; i++) {
      const u = i / N;
      const phi = phi0 + s * u * TAU * 1.2;
      const rho = r * 0.44 * (1 - 0.72 * u);
      pts.push([cx + Math.cos(phi) * rho, cy + Math.sin(phi) * rho, u]);
    }
    for (const pass of [0, 1]) {
      for (let i = 0; i < N; i++) {
        const [x0, y0, u] = pts[i], [x1, y1] = pts[i + 1];
        const w = r * (0.4 - 0.27 * u);
        ctx.strokeStyle = pass ? mix("#efe3c2", "#8a7650", u) : "#4e3f22";
        ctx.lineWidth = pass ? w : w + r * 0.12;
        ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(x1, y1); ctx.stroke();
      }
    }
    // growth ridges across the horn
    ctx.strokeStyle = "rgba(78,63,34,.55)"; ctx.lineWidth = lw(r, 0.04);
    for (let i = 2; i < N - 4; i += 3) {
      const [x0, y0, u] = pts[i], [x1, y1] = pts[i + 1];
      const dx = x1 - x0, dy = y1 - y0, d = Math.hypot(dx, dy) || 1;
      const h = r * (0.4 - 0.27 * u) * 0.45;
      ctx.beginPath(); ctx.moveTo(x0 - (dy / d) * h, y0 + (dx / d) * h); ctx.lineTo(x0 + (dy / d) * h, y0 - (dx / d) * h); ctx.stroke();
    }
  }
  helm(ctx, r);
  disc(ctx, 0, r * 0.44, r * 0.13, RUBY);
};

// V2. Jarl: a gold-trimmed helm with a riveted crest and tall straight horns
// capped in gold; a glint walks up the crest.
export const jarl: CrownDraw = (ctx, r, dir, t) => {
  ctx.rotate(dir + HALF);
  ctx.lineJoin = "round";
  for (const s of [-1, 1]) {
    ctx.fillStyle = hornFill(ctx, s * r * 0.5, -r * 0.3, s * r * 0.82, -r * 1.45, "#a99068");
    ctx.strokeStyle = "#4e3f22"; ctx.lineWidth = lw(r, 0.08);
    ctx.beginPath();
    ctx.moveTo(s * r * 0.36, -r * 0.3);
    ctx.quadraticCurveTo(s * r * 0.5, -r * 0.95, s * r * 0.82, -r * 1.45);
    ctx.quadraticCurveTo(s * r * 0.9, -r * 0.85, s * r * 0.8, -r * 0.15);
    ctx.closePath(); ctx.fill(); ctx.stroke();
    // gold cap on the point and a gold ring round the horn
    ctx.fillStyle = GOLD; ctx.strokeStyle = INK; ctx.lineWidth = lw(r, 0.06);
    ctx.beginPath(); ctx.moveTo(s * r * 0.64, -r * 1.12); ctx.lineTo(s * r * 0.82, -r * 1.45); ctx.lineTo(s * r * 0.87, -r * 1.08); ctx.closePath(); ctx.fill(); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(s * r * 0.45, -r * 0.6); ctx.lineTo(s * r * 0.88, -r * 0.62); ctx.lineWidth = r * 0.14; ctx.strokeStyle = INK; ctx.stroke();
    ctx.lineWidth = r * 0.07; ctx.strokeStyle = GOLD; ctx.stroke();
  }
  helm(ctx, r, { trim: true, crest: true });
  disc(ctx, 0, r * 0.44, r * 0.15, INK);
  disc(ctx, 0, r * 0.44, r * 0.11, RUBY);
  const p = (t * 0.5) % 1;
  sparkle(ctx, 0, r * 0.2 - p * r * 1.0, r * 0.3, Math.sin(p * Math.PI));
};

// V3. Scarred: one horn whole, the other snapped off jagged; a darker, dented
// helm missing a rivet. A champion with stories.
const scarred: CrownDraw = (ctx, r, dir, t) => {
  ctx.rotate(dir + HALF);
  sweptHorn(ctx, r, -1, 0.9);
  const s = 1;
  ctx.fillStyle = hornFill(ctx, s * r * 0.7, 0, s * r * 1.3, -r * 0.65);
  ctx.strokeStyle = "#4e3f22"; ctx.lineWidth = lw(r, 0.08); ctx.lineJoin = "round";
  ctx.beginPath();
  ctx.moveTo(s * r * 0.72, r * 0.15);
  ctx.quadraticCurveTo(s * r * 1.45, r * 0.02, s * r * 1.34, -r * 0.6);
  ctx.lineTo(s * r * 1.24, -r * 0.5); ctx.lineTo(s * r * 1.17, -r * 0.68); ctx.lineTo(s * r * 1.06, -r * 0.5);
  ctx.quadraticCurveTo(s * r * 1.0, -r * 0.36, s * r * 0.6, -r * 0.42);
  ctx.closePath(); ctx.fill(); ctx.stroke();
  // the fresh break, paler than the weathered horn
  ctx.fillStyle = "#fbf4e2";
  ctx.beginPath(); ctx.moveTo(s * r * 1.32, -r * 0.57); ctx.lineTo(s * r * 1.24, -r * 0.5); ctx.lineTo(s * r * 1.17, -r * 0.65); ctx.lineTo(s * r * 1.08, -r * 0.5); ctx.lineTo(s * r * 1.2, -r * 0.45); ctx.closePath(); ctx.fill();
  helm(ctx, r, { worn: true });
  disc(ctx, 0, r * 0.44, r * 0.13, RUBY);
  const p = (t * 0.25) % 1;
  if (p < 0.3) sparkle(ctx, -r * 0.55 + p * r * 3, -r * 0.3, r * 0.26, Math.sin((p / 0.3) * Math.PI));
};

// V4. Valkyrie: feathered wings instead of horns, white with gold shafts,
// beating very slowly; a gold-trimmed helm.
const valkyrie: CrownDraw = (ctx, r, dir, t) => {
  ctx.rotate(dir + HALF);
  const flap = 0.09 * Math.sin(t * 1.6);
  const lens = [0.95, 0.88, 0.76, 0.62];
  for (const s of [-1, 1]) {
    const bx = s * r * 0.7, by = -r * 0.02;
    for (let k = lens.length - 1; k >= 0; k--) {
      // the longest feather sweeps furthest forward
      const a = s > 0 ? -1.25 + k * 0.32 - flap : Math.PI + 1.25 - k * 0.32 + flap;
      const L = r * lens[k];
      const cx = bx + Math.cos(a) * L * 0.5, cy = by + Math.sin(a) * L * 0.5;
      ctx.save(); ctx.translate(cx, cy); ctx.rotate(a);
      ctx.fillStyle = k % 2 ? "#e6ecf4" : "#f8fbff"; ctx.strokeStyle = "#5d6878"; ctx.lineWidth = lw(r, 0.06);
      ctx.beginPath();
      ctx.moveTo(-L * 0.5, 0);
      ctx.quadraticCurveTo(-L * 0.1, -r * 0.24, L * 0.5, -r * 0.03);
      ctx.quadraticCurveTo(L * 0.1, r * 0.2, -L * 0.5, 0);
      ctx.closePath(); ctx.fill(); ctx.stroke();
      ctx.strokeStyle = GOLD_DARK; ctx.lineWidth = lw(r, 0.04);
      ctx.beginPath(); ctx.moveTo(-L * 0.45, 0); ctx.lineTo(L * 0.42, -r * 0.03); ctx.stroke();
      ctx.restore();
    }
    disc(ctx, bx, by, r * 0.16, INK);
    disc(ctx, bx, by, r * 0.11, GOLD);
  }
  helm(ctx, r, { trim: true });
  disc(ctx, 0, r * 0.44, r * 0.13, "#7fd8ff");
};

// V5. Chieftain: shorter swept horns, a horsehair plume in the worm's colour
// swaying on top and two braids with gold beads hanging behind.
const chieftain: CrownDraw = (ctx, r, dir, t, color) => {
  ctx.rotate(dir + HALF);
  ctx.lineCap = "round";
  // braids
  for (const s of [-1, 1]) {
    for (let i = 0; i < 5; i++) {
      const sw = Math.sin(t * 1.7 + i * 0.5 + s) * r * 0.03 * i;
      const x = s * r * (0.78 + i * 0.02) + sw, y = r * (0.66 + i * 0.15);
      ctx.fillStyle = i % 2 ? "#c99a3c" : "#e8c06a"; ctx.strokeStyle = "#6b4a14"; ctx.lineWidth = lw(r, 0.04);
      ctx.beginPath(); ctx.ellipse(x, y, r * 0.11, r * 0.12, s * 0.4, 0, TAU); ctx.fill(); ctx.stroke();
    }
    disc(ctx, s * r * 0.82 + Math.sin(t * 1.7 + 2.5 + s) * r * 0.08, r * 1.36, r * 0.1, GOLD);
  }
  sweptHorn(ctx, r, -1, 0.75);
  sweptHorn(ctx, r, 1, 0.75);
  // plume: strands fanning from a gold socket at the top of the dome
  const sway = Math.sin(t * 1.4) * r * 0.1;
  const plume = () => {
    ctx.beginPath();
    ctx.moveTo(-r * 0.16, -r * 0.62);
    ctx.quadraticCurveTo(-r * 0.5 + sway, -r * 1.05, -r * 0.28 + sway * 1.4, -r * 1.5);
    ctx.quadraticCurveTo(sway * 1.5, -r * 1.62, r * 0.28 + sway * 1.4, -r * 1.5);
    ctx.quadraticCurveTo(r * 0.5 + sway, -r * 1.05, r * 0.16, -r * 0.62);
    ctx.closePath();
  };
  plume(); ctx.fillStyle = color; ctx.fill();
  ctx.strokeStyle = darken(color, 0.55); ctx.lineWidth = lw(r, 0.07); ctx.lineJoin = "round"; ctx.stroke();
  ctx.strokeStyle = lighten(color, 0.35); ctx.lineWidth = lw(r, 0.04);
  for (const k of [-0.5, 0, 0.5]) {
    ctx.beginPath(); ctx.moveTo(k * r * 0.2, -r * 0.7); ctx.quadraticCurveTo(k * r * 0.4 + sway, -r * 1.1, k * r * 0.45 + sway * 1.4, -r * 1.45); ctx.stroke();
  }
  helm(ctx, r, { crest: true });
  disc(ctx, 0, -r * 0.66, r * 0.16, INK);
  disc(ctx, 0, -r * 0.66, r * 0.11, GOLD);
  disc(ctx, 0, r * 0.44, r * 0.13, lighten(color, 0.1));
};

export const VIKING_VARIANTS: CrownVariant[] = [
  { id: "V1", name: "Ram", idea: "ridged horns curling into spirals", draw: ram },
  { id: "V2", name: "Jarl", idea: "gold trim, riveted crest, tall gold-capped horns", draw: jarl },
  { id: "V3", name: "Scarred", idea: "one horn snapped off, dented helm", draw: scarred },
  { id: "V4", name: "Valkyrie", idea: "slow-beating feathered wings", draw: valkyrie },
  { id: "V5", name: "Chieftain", idea: "plume in the worm's colour, braids behind", draw: chieftain },
];
